import { PlusIcon, MinusIcon } from "@heroicons/react/16/solid";
import GripHandle from "../../../../../../componentLibrary/GripHandle";
import Card from "../../../../../../componentLibrary/Card";
import Input from "../../../../../../componentLibrary/Input";
import Checkbox from "../../../../../../componentLibrary/Checkbox";
import Button from "../../../../../../componentLibrary/Button";
import type {
  MultipleChoiceQuestionI,
  ToolboxQuestionI,
} from "../../../../../../types";

export default function MultipleChoiceQuestionCard({
  question,
  onChange,
  onRemove,
  index,
}: {
  question: MultipleChoiceQuestionI;
  onChange: (q: ToolboxQuestionI) => void;
  onRemove?: () => void;
  index?: number;
}) {
  const choices: string[] = question.content.choices ?? [];

  const updateField = (field: string, value: unknown) =>
    onChange({
      ...question,
      content: { ...question.content, [field]: value },
    } as ToolboxQuestionI);

  const updateChoice = (index: number, value: string) => {
    const updated = [...choices];
    updated[index] = value;
    updateField("choices", updated);
  };

  const addChoice = () => updateField("choices", [...choices, ""]);

  const removeLastChoice = () => {
    if (choices.length > 1) updateField("choices", choices.slice(0, -1));
  };

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <GripHandle className="fill-gray-300 shrink-0" />
          {index !== undefined
            ? `Q${index + 1} · Multiple Choice`
            : "Multiple Choice"}
        </span>
      }
      onClose={onRemove}
    >
      <div className="space-y-2 mt-1">
        <Input
          label="Variable name"
          placeholder="e.g. sleep_hours_needed"
          value={question.variableName}
          onChange={(e) =>
            onChange({
              ...question,
              variableName: e.target.value,
            } as ToolboxQuestionI)
          }
        />
        <Input
          label="Label"
          placeholder="e.g. Which best describes your mood?"
          value={question.content?.label ?? ""}
          onChange={(e) => updateField("label", e.target.value)}
        />

        {choices.map((choice, i) => (
          <Input
            key={i}
            label={`Choice ${i + 1}`}
            placeholder={`Option ${i + 1}`}
            value={choice}
            onChange={(e) => updateChoice(i, e.target.value)}
          />
        ))}

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={removeLastChoice}
            disabled={choices.length <= 1}
          >
            <MinusIcon className="w-4 h-4" />
          </Button>
          <Button variant="dark" size="sm" onClick={addChoice}>
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>

        <Checkbox
          label="Allow multiple selections"
          checked={question.content?.msq ?? false}
          onChange={(e) => updateField("msq", e.target.checked)}
        />
      </div>
    </Card>
  );
}
